import cx from "classnames";
import { Flex, Text, List, Button } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import FieldToggleMenu from "@/appComponents/Blocks/Menus/FieldToggleMenu/FieldToggleMenu";
import MultiClassFieldToggleMenu, {
  createCompositeFieldId
} from "@/appComponents/Blocks/Menus/FieldToggleMenu/MultiClassFieldToggleMenu";
import { enumObjClassNames } from "@coreapp/constants";
import { enumRuleSets } from "../RowLevelSecurityModal.utils";
interface FieldsSectionProps {
  editProfile: {
    allowedFields: string[];
    restrictedFields: string[];
    conditionalFields: object[];
    pos_allowedFields: string[];
    pos_restrictedFields: string[];
  };
  selectedFields: any;
  ToggleField: (field: { value: string }) => void;
  isPositions?: boolean;
  headers?: string[];
  fieldDefs?: any;
  disabledFields?: string[];
  currentRuleSet?: (typeof enumRuleSets)[keyof typeof enumRuleSets];
  HandleResetFields?: () => void;
}

export const FieldsSection = ({
  editProfile,
  selectedFields,
  ToggleField,
  isPositions,
  headers,
  fieldDefs,
  disabledFields,
  currentRuleSet,
  HandleResetFields
}: FieldsSectionProps) => {
  const HandleFieldToggle = (target: { label: string; value: string }) => {
    const targetField = target;
    // Multiclass field toggle value = [className]::[fieldName]
    if (isPositions) {
      const className = targetField.value.split("::")[0];
      const fieldName = targetField.value.split("::")[1];
      const update_field = {
        ...targetField,
        className: className,
        value: fieldName
      };
      ToggleField(update_field);
    } else {
      ToggleField(targetField);
    }
  };

  const BuildCurrentFields = (fields: string[], className: string) => {
    let fieldOptions = [];

    for (let field of fields) {
      let option = {
        value: field,
        className: className,
        content: (
          <ActionListItem
            label={field}
            labelProps={{ size: "small" }}
            actions={[
              {
                iconName: "trash-can",
                buttonProps: { color: "error" },
                onClick: () => ToggleField(option),
                dataTestId: "account-settings-security-rls-field-remove-btn"
              }
            ]}
          />
        )
      };

      fieldOptions.push(option);
    }
    return fieldOptions;
  };

  const GetFieldDefFromList = (field: string, className: string) => {
    return fieldDefs?.find(
      (def: any) => def.GetFieldName() === field && def.GetFieldClassName() === className
    );
  };

  const BuildFieldOptionsByClass = (className: string) => {
    if (!selectedFields || !isPositions) return [];

    const filteredFields = selectedFields[className]
      .filter((field: string) => {
        const fieldDef = GetFieldDefFromList(field, className);
        return !!fieldDef;
      })
      .sort();

    return BuildCurrentFields(filteredFields, className);
  };

  const GetFieldToggleHeader = () => {
    if (currentRuleSet === enumRuleSets.allowed) {
      return appstr("rlsallowedtoggleinfo");
    } else if (currentRuleSet === enumRuleSets.restricted) {
      return appstr("rlsrestrictedtoggleinfo");
    } else if (currentRuleSet === enumRuleSets.conditional) {
      return appstr("rlsconditionaltoggleinfo");
    }
    return "";
  };

  const GetDisabledFieldsByRule = () => {
    if (currentRuleSet === enumRuleSets.allowed) {
      return {
        [enumObjClassNames.box]: editProfile?.restrictedFields || [],
        [enumObjClassNames.position]: editProfile?.pos_restrictedFields || []
      };
    } else if (currentRuleSet === enumRuleSets.restricted) {
      return {
        [enumObjClassNames.box]: editProfile?.allowedFields || [],
        [enumObjClassNames.position]: editProfile?.pos_allowedFields || []
      };
    }
    return {};
  };

  const GetDisabledMessage = (field: string) => {
    if (disabledFields?.includes(field)) {
      return appstr("Core mapped fields must always show to build your base chart");
    }

    let status1, status2;

    if (currentRuleSet === enumRuleSets.allowed && editProfile?.restrictedFields?.includes(field)) {
      status1 = appstr("Allowed");
      status2 = appstr("Restricted");
    } else if (
      currentRuleSet === enumRuleSets.restricted &&
      editProfile?.allowedFields?.includes(field)
    ) {
      status1 = appstr("Restricted");
      status2 = appstr("Allowed");
    }
    return appstr("This field cannot be {status1} as it has already been selected as {status2}", {
      status1: status1 ? status1.toLowerCase() : "",
      status2: status2 ? status2.toLowerCase() : ""
    });
  };

  const fieldTrigger = (
    <Button
      size={"medium"}
      className={cx("oc-special-secondary", "oc-special-secondary--fit")}
      modifier={"secondary"}
      dataTestId="account-settings-security-rls-choose-fields-btn"
    >
      {appstr("Choose fields")}
    </Button>
  );

  const GetCurrentDisabledFieldsList = () => {
    //return composite IDs for multi class field toggle
    let currentDisabledFields: string[] = [];
    const disabledFieldsByRule = GetDisabledFieldsByRule();
    const disabledFieldsBox = disabledFieldsByRule[enumObjClassNames.box] || [];
    const disabledFieldsPosition = disabledFieldsByRule[enumObjClassNames.position] || [];
    disabledFieldsBox.forEach(field => {
      currentDisabledFields.push(createCompositeFieldId(field, enumObjClassNames.box));
    });
    disabledFieldsPosition.forEach(field => {
      currentDisabledFields.push(createCompositeFieldId(field, enumObjClassNames.position));
    });

    currentDisabledFields.push(...(disabledFields || []));

    return currentDisabledFields;
  };

  const ResetFields = () => {
    if (!HandleResetFields || !selectedFields) return;
    HandleResetFields();
  };

  const GetMultiClassSelectedFieldsList = (selectedFieldsObj: Record<string, string[]>) => {
    // multiclass field toggle compositeID format [className]::[fieldName]
    const selectedFields: string[] = [];
    for (const className in selectedFieldsObj) {
      if (selectedFieldsObj[className]?.length > 0) {
        selectedFieldsObj[className].forEach((field: string) => {
          selectedFields.push(createCompositeFieldId(field, className));
        });
      }
    }

    return selectedFields;
  };

  return (
    <Flex direction={"column"} gap={"4px"} width={"100%"} height={"100%"}>
      <Flex justifyContent={"space-between"} gap={"4px"} alignItems={"center"} width={"100%"}>
        <Text modifier={"bold"} size={"medium"}>
          {"Fields"}
        </Text>
        <Flex className={"oc-right-justify"}>
          {isPositions ? (
            <MultiClassFieldToggleMenu
              selectedFieldIds={GetMultiClassSelectedFieldsList(selectedFields)}
              onChange={HandleFieldToggle}
              small={false}
              trigger={fieldTrigger}
              dataTestIdPrefix={"rls-modal-field-toggle-"}
              fieldDefs={fieldDefs}
              useFieldNames={true}
              disabledFieldIds={GetCurrentDisabledFieldsList()}
              listHeader={{
                label: GetFieldToggleHeader(),
                postSearchNode: (
                  <Flex width={"100%"} justifyContent={"end"} padding={"4px 8px"}>
                    <Button
                      modifier="secondary"
                      type="button"
                      size="small"
                      onClick={ResetFields}
                      dataTestId="account-settings-security-rls-fields-reset-btn"
                    >
                      {appstr("Reset")}
                    </Button>
                  </Flex>
                )
              }}
              getHoverTipContent={GetDisabledMessage}
            />
          ) : (
            <FieldToggleMenu
              selectedFieldNames={selectedFields?.[enumObjClassNames.box]?.sort() ?? []}
              fieldNames={headers ? headers.sort() : []}
              onChange={HandleFieldToggle}
              trigger={fieldTrigger}
              listHeader={{
                label: appstr("Toggle fields to include")
              }}
              position="bottom-end"
              listWidth={"large"}
              stayAlive={true}
              disabledFieldIds={[
                ...(disabledFields ?? []),
                ...(GetDisabledFieldsByRule()?.[enumObjClassNames.box] ?? [])
              ]}
              getHoverTipContent={GetDisabledMessage}
              small={undefined}
              dataTestIdPrefix={undefined}
              localize={undefined}
            />
          )}
        </Flex>
      </Flex>
      <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
        {isPositions ? (
          <Flex gap="16px" width="100%">
            <Flex direction="column" width="50%">
              <Text modifier="bold" size="small" style={{ marginBottom: "8px" }}>
                {appstr("Employee fields")}
              </Text>
              <List
                enableVirtualization={false}
                coloredSelection={true}
                showCheck={false}
                value={0}
                optionSize={"small"}
                options={BuildFieldOptionsByClass(enumObjClassNames.box)}
                listWidth="fill"
              />
            </Flex>
            <Flex direction="column" width="50%">
              <Text modifier="bold" size="small" style={{ marginBottom: "8px" }}>
                {appstr("Position fields")}
              </Text>
              <List
                enableVirtualization={false}
                coloredSelection={true}
                showCheck={false}
                value={0}
                optionSize={"small"}
                options={BuildFieldOptionsByClass(enumObjClassNames.position)}
                listWidth="fill"
              />
            </Flex>
          </Flex>
        ) : (
          <List
            enableVirtualization={false}
            coloredSelection={true}
            showCheck={false}
            value={0}
            optionSize={"small"}
            options={BuildCurrentFields(
              selectedFields?.[enumObjClassNames.box] ?? [],
              enumObjClassNames.box
            )}
            listWidth="fill"
          />
        )}
      </div>
    </Flex>
  );
};
